import { Box, Stack } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { SafeEntriesPanel } from './BeforeAfterPanel';
import { toAuditPageError } from './AuditLogPage';
import { auditRepositoryToken } from './repository';

export function SecurityEventDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { eventId = '' } = useParams();
  const repository = useRepository(auditRepositoryToken);
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refresh, setRefresh] = useState(0);
  const resource = useResource(
    (signal) => repository.getSecurityEvent(eventId, signal),
    [repository, eventId, refresh],
    () => false,
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toAuditPageError(resource.error, t('securityEvents.errors.loadDetail'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              backTo={paths.securityEvents}
              dataSource={dataSource}
              subtitle={`${resource.data.category} · ${resource.data.severity}`}
              title={resource.data.id}
            />
            <MetadataList
              items={[
                {
                  label: t('securityEvents.fields.occurredAt'),
                  value: `${formatTimestamp(resource.data.occurredAt, 'Asia/Bangkok', language).display} (${formatTimestamp(resource.data.occurredAt, 'Asia/Bangkok', language).originalOffset})`,
                },
                { label: t('securityEvents.fields.category'), value: resource.data.category },
                { label: t('securityEvents.fields.severity'), value: resource.data.severity },
                { label: t('securityEvents.fields.eventCode'), value: resource.data.eventCode },
                {
                  label: t('securityEvents.fields.principalActorId'),
                  value: resource.data.principalActorId ?? t('common.notAvailable'),
                },
                {
                  label: t('securityEvents.fields.principalRole'),
                  value: resource.data.principalRole ?? t('common.notAvailable'),
                },
                {
                  label: t('securityEvents.fields.serviceActorId'),
                  value: resource.data.serviceActorId,
                },
                {
                  label: t('securityEvents.fields.partnerId'),
                  value: resource.data.partnerId ?? t('common.notAvailable'),
                },
                {
                  label: t('securityEvents.fields.agentId'),
                  value: resource.data.agentId ?? t('common.notAvailable'),
                },
                {
                  label: t('securityEvents.fields.credentialActorId'),
                  value: resource.data.credentialActorId ?? t('common.notAvailable'),
                },
                {
                  label: t('securityEvents.fields.requestId'),
                  value: resource.data.publicRequestId ?? t('common.notAvailable'),
                },
                {
                  label: t('securityEvents.fields.sourceIp'),
                  value: resource.data.sourceIp ?? t('common.notAvailable'),
                },
              ]}
            />
            <SafeEntriesPanel
              record={resource.data.details}
              title={t('securityEvents.sections.details')}
            />
          </Stack>
        )}
      </PageState>
    </Box>
  );
}
